import * as React from 'react';

import { Button as BaseButton } from '@base-ui/react/button';

import { cn } from '@/lib/cn';

/**
 * Button variant styles - industrial/utilitarian aesthetic.
 * Clean borders, subtle shadows, focus on functionality.
 */
const buttonVariants = {
  primary: 'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80',
  secondary: 'bg-muted text-foreground hover:bg-muted/80 active:bg-muted/70 border border-border',
  destructive:
    'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80',
  ghost: 'hover:bg-muted active:bg-muted/70 text-foreground',
  outline: 'border border-border bg-background hover:bg-muted active:bg-muted/70 text-foreground',
} as const;

const buttonSizes = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
  icon: 'size-10',
  'icon-sm': 'size-8',
} as const;

export type ButtonVariant = keyof typeof buttonVariants;
export type ButtonSize = keyof typeof buttonSizes;

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style variant */
  variant?: ButtonVariant;
  /** Button size */
  size?: ButtonSize;
  /** Loading state - disables button and shows loading indicator */
  loading?: boolean;
  /** Accessible label for icon-only buttons (required when size is 'icon' or 'icon-sm') */
  'aria-label'?: string;
  children?: React.ReactNode;
}

/**
 * Button component built on Base UI for accessible keyboard/focus behavior.
 *
 * @example
 * // Primary button
 * <Button variant="primary">Save</Button>
 *
 * @example
 * // Icon-only button (MUST have aria-label)
 * <Button variant="ghost" size="icon" aria-label="Close">
 *   <XIcon />
 * </Button>
 *
 * @example
 * // Loading state
 * <Button loading>Saving...</Button>
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = 'primary', size = 'md', loading = false, disabled, children, ...props },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    // Extract props that may have compatibility issues with exactOptionalPropertyTypes
    const { style, ...restProps } = props;

    return (
      <BaseButton
        ref={ref}
        disabled={isDisabled}
        {...(style !== undefined ? { style } : {})}
        className={cn(
          // Base styles
          'inline-flex items-center justify-center gap-2 rounded-md font-medium',
          // Transition - keep it fast per ui-skills (NEVER exceed 200ms)
          ' duration-150',
          // Focus ring - accessible focus visible
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          // Disabled state
          'disabled:pointer-events-none disabled:opacity-50',
          // Variant and size
          buttonVariants[variant],
          buttonSizes[size],
          className,
        )}
        {...restProps}
      >
        {loading ? (
          <>
            <LoadingSpinner />
            {children}
          </>
        ) : (
          children
        )}
      </BaseButton>
    );
  },
);

Button.displayName = 'Button';

/**
 * Simple loading spinner - animates opacity only (compositor prop).
 * Does NOT animate layout properties per ui-skills.
 */
function LoadingSpinner() {
  return (
    <svg
      className="size-4 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

export { buttonVariants, buttonSizes };
